import React from 'react';
import { 
  TextField, 
  FormControl, 
  FormControlLabel, 
  Checkbox, 
  Radio, 
  RadioGroup, 
  Select, 
  MenuItem, 
  InputLabel,
  FormHelperText,
  Box,
  Typography
} from '@mui/material';

const InputGroup = ({ 
  title, 
  children, 
  spacing = 2, 
  sx = {} 
}) => {
  return (
    <Box sx={{ mb: 3, ...sx }}>
      {title && (
        <Typography variant="h6" sx={{ mb: 2, color: 'primary.main' }}>
          {title}
        </Typography>
      )}
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: spacing }}>
        {children}
      </Box>
    </Box>
  );
};

// Custom TextField with Formik integration
export const FormTextField = ({ 
  formik, 
  name, 
  label, 
  type = 'text', 
  required = false,
  fullWidth = true,
  ...props 
}) => {
  const hasError = formik.touched[name] && formik.errors[name];
  
  return (
    <TextField
      name={name}
      label={label}
      type={type}
      value={formik.values[name] || ''}
      onChange={formik.handleChange}
      onBlur={formik.handleBlur}
      error={!!hasError}
      helperText={hasError}
      required={required}
      fullWidth={fullWidth}
      variant="outlined"
      size="small"
      {...props}
    />
  );
};

// Custom Checkbox with Formik integration
export const FormCheckbox = ({ 
  formik, 
  name, 
  label, 
  ...props 
}) => {
  const handleChange = (event) => {
    // Convert checkbox value to boolean
    const value = event.target.checked;
    formik.setFieldValue(name, value);
  };

  return (
    <FormControlLabel
      control={
        <Checkbox
          name={name}
          checked={Boolean(formik.values[name])}
          onChange={handleChange}
          {...props}
        />
      }
      label={label}
    />
  );
};

// Custom Radio Group with Formik integration
export const FormRadioGroup = ({ 
  formik, 
  name, 
  label, 
  options, 
  ...props 
}) => {
  const hasError = formik.touched[name] && formik.errors[name];
  
  return (
    <FormControl error={!!hasError} fullWidth>
      <Typography variant="subtitle2" sx={{ mb: 1 }}>
        {label}
      </Typography>
      <RadioGroup
        name={name}
        value={formik.values[name] || ''}
        onChange={formik.handleChange}
        {...props}
      >
        {options.map((option) => (
          <FormControlLabel
            key={option.value}
            value={option.value}
            control={<Radio />}
            label={option.label}
          />
        ))}
      </RadioGroup>
      {hasError && <FormHelperText>{formik.errors[name]}</FormHelperText>}
    </FormControl>
  );
};

// Custom Select with Formik integration
export const FormSelect = ({ 
  formik, 
  name, 
  label, 
  options, 
  required = false,
  fullWidth = true,
  ...props 
}) => {
  const hasError = formik.touched[name] && formik.errors[name];
  
  return (
    <FormControl error={!!hasError} fullWidth={fullWidth} size="small">
      <InputLabel>{label}</InputLabel>
      <Select
        name={name}
        value={formik.values[name] || ''}
        onChange={formik.handleChange}
        onBlur={formik.handleBlur}
        label={label}
        required={required}
        {...props}
      >
        {options.map((option) => (
          <MenuItem key={option.value} value={option.value}>
            {option.label}
          </MenuItem>
        ))}
      </Select>
      {hasError && <FormHelperText>{formik.errors[name]}</FormHelperText>}
    </FormControl>
  );
};

// Conditional Field Wrapper
export const ConditionalField = ({ 
  condition, 
  children 
}) => {
  if (!condition) return null;
  return <>{children}</>;
};

export default InputGroup; 